/**
 * Shared editor control: insert an image by pasting its URL.
 *
 * A toggleable inline form for blocks whose image comes from MediaUpload, which
 * only offers the media library. Callers own the attribute shape — this just
 * hands back the trimmed URL.
 *
 * Deliberately usable outside MediaUploadCheck: inserting by link never touches
 * the media library, so it needs no upload rights — the same split core makes
 * in MediaPlaceholder.
 *
 * Styles are inline so the control needs no per-block stylesheet entry.
 */

import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { Button, TextControl } from '@wordpress/components';

const FORM_STYLE = {
	display: 'flex',
	alignItems: 'flex-end',
	gap: '8px',
	width: '100%',
	marginTop: '8px',
};

const FIELD_STYLE = { flex: 1, minWidth: 0 };

/**
 * @param {Object}   props
 * @param {Function} props.onSubmit Called with the trimmed URL string.
 * @return {JSX.Element} Insert-from-URL control.
 */
export default function InsertImageFromUrl( { onSubmit } ) {
	const [ isOpen, setIsOpen ] = useState( false );
	const [ url, setUrl ] = useState( '' );

	const submit = ( event ) => {
		event.preventDefault();
		const trimmed = url.trim();
		if ( ! trimmed ) {
			return;
		}
		onSubmit( trimmed );
		setUrl( '' );
		setIsOpen( false );
	};

	return (
		<>
			<Button
				variant="link"
				onClick={ () => setIsOpen( ( open ) => ! open ) }
				aria-expanded={ isOpen }
			>
				{ __( 'Insert from URL', 'brandy-blocks' ) }
			</Button>
			{ isOpen && (
				<form style={ FORM_STYLE } onSubmit={ submit }>
					<div style={ FIELD_STYLE }>
						<TextControl
							__nextHasNoMarginBottom
							type="url"
							label={ __( 'Image URL', 'brandy-blocks' ) }
							hideLabelFromVision
							placeholder={ __(
								'Paste an image URL',
								'brandy-blocks'
							) }
							value={ url }
							onChange={ setUrl }
						/>
					</div>
					<Button
						variant="primary"
						type="submit"
						disabled={ ! url.trim() }
					>
						{ __( 'Apply', 'brandy-blocks' ) }
					</Button>
				</form>
			) }
		</>
	);
}
